import { cloneElement, type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useLocation } from 'react-router-dom';

import { PwaUpdateContext } from '@/components/pwa/pwaUpdateContext';
import { UPDATE_IDLE_MS, usePwaUpdateHeld } from '@/components/pwa/pwaUpdateHold';
import { usePwaUpdate } from '@/components/pwa/usePwaUpdate';
import { I, type IconProps } from '@/components/ri/icons';
import { isPaymentPage } from '@/lib/paymentTelemetryPrivacy';

const TOAST_ID = 'pwa-update-ready';
const ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const;

export function PwaUpdateProvider({ children }: { children: ReactNode }) {
  const onCustomerSurface = isPaymentPage(useLocation().pathname);
  const state = usePwaUpdate(!onCustomerSurface);
  const { needRefresh } = state;
  const toasted = useRef(false);
  const toastVisible = useRef(false);
  const held = usePwaUpdateHeld();
  const [idle, setIdle] = useState(false);
  const waiting = needRefresh && held && !onCustomerSurface;

  // While a held screen keeps the toast back, it still goes once the rep stops touching the phone.
  useEffect(() => {
    if (!waiting) return;
    let timer = window.setTimeout(() => setIdle(true), UPDATE_IDLE_MS);
    const active = () => {
      setIdle(false);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setIdle(true), UPDATE_IDLE_MS);
    };
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, active, { passive: true }));
    return () => {
      window.clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, active));
      // The next held screen starts its own inactivity window.
      setIdle(false);
    };
  }, [waiting]);

  // One attention grab per detected build; afterwards the header indicator is the only reminder.
  // A toast still on screen is pulled when the route moves onto a customer surface or a scan opens,
  // and it waits while a scan or capture is open until the rep is back on the event home or idle.
  // The page never reloads on its own: only the rep's Refresh does.
  const deferred = held && !idle;
  useEffect(() => {
    if (onCustomerSurface || deferred) {
      notifications.hide(TOAST_ID);
      // A toast cut short by a scan comes back once the scan is done; one that already ran its course does not.
      if (deferred && toastVisible.current) toasted.current = false;
      toastVisible.current = false;
      return;
    }
    if (!needRefresh || toasted.current) return;
    toasted.current = true;
    toastVisible.current = true;
    notifications.show({
      id: TOAST_ID,
      icon: cloneElement(I.refresh as ReactElement<IconProps>, { size: 14 }),
      color: 'var(--accent)',
      title: 'New version ready',
      message: "Finish what you're on, then refresh from the header whenever.",
      autoClose: 8000,
      onClose: () => { toastVisible.current = false; },
    });
  }, [needRefresh, onCustomerSurface, deferred]);

  return <PwaUpdateContext.Provider value={state}>{children}</PwaUpdateContext.Provider>;
}
